import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { withLocalErrorHandling } from "../../../../../api/handleErrorLocally";
import { formatShortDate } from "../../../../../format/formatDate";
import { useClaimsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { useOptionalClaimSelection } from "../../../context/ClaimSelectionContext";
import { useClientClaims } from "../../../hooks/useClientClaims";
import { processableClaimIdsOf } from "../../../utils/claimSelection";
import { carryForwardRowsOf } from "../CarryForwardBalancesDialog.utils";

export function useCarryForwardBalances(clientId: string, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const claimsApi = useClaimsApi();
  const queryClient = useQueryClient();
  const selection = useOptionalClaimSelection();

  // Read once rather than per render, so the overlay keeps deciding against the date it was opened
  // with instead of moving under the user at midnight.
  const [today] = useState(() => new Date());

  // The same query the list reads, so opening the overlay serves from cache rather than refetching.
  const { data: claims = [], isLoading } = useClientClaims(clientId);

  const rows = carryForwardRowsOf(claims, selection?.selectedClaimIds ?? new Set(), today);
  const processableClaimIds = processableClaimIdsOf(rows);
  const skippedCount = rows.length - processableClaimIds.length;

  const carryForwardMutation = useMutation({
    mutationFn: (claimIds: string[]) => {
      if (!claimsApi.carryForwardClaimBalances) {
        throw new Error("ClientFileApi.claims.carryForwardClaimBalances is not available");
      }
      return claimsApi.carryForwardClaimBalances(
        { clientId, carryForwardClaimBalancesRequest: { claimIds } },
        withLocalErrorHandling
      );
    },
    onSuccess: (response) => {
      // Every affected claim gained an entry, so each claim's own cache entry is stale too, not just
      // the list.
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById], refetchType: "active" });

      const date = formatShortDate(response.date);
      showSuccess(
        response.skippedClaimIds.length > 0
          ? t("claims.carryForward.successfullyCarriedForwardPartially", {
              count: response.affectedClaimCount,
              selected: rows.length,
              // Everything the selection did not gain an entry for, whether this overlay excluded it
              // or the server did — the response reports only its own. Counted against the selection
              // so the three figures of the message add up.
              skipped: rows.length - response.affectedClaimCount,
              date,
            })
          : t("claims.carryForward.successfullyCarriedForward", { count: response.affectedClaimCount, date })
      );

      selection?.clearSelection();
      onClose();
    },
    onError: () => {
      // Nothing was written, so the overlay stays open with the selection intact and the user can
      // try again. The list is refetched because a rejected request usually means it is out of date.
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      showError(t("claims.carryForward.failed"));
    },
  });

  const confirm = async () => {
    if (processableClaimIds.length === 0) {
      return;
    }

    try {
      await carryForwardMutation.mutateAsync(processableClaimIds);
    } catch {
      // Reported by the mutation's error handler; the dialog stays open.
    }
  };

  return {
    today,
    rows,
    processableCount: processableClaimIds.length,
    skippedCount,
    confirm,
    isLoading,
    isSubmitting: carryForwardMutation.isPending,
  };
}
